import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, getArticlesByCategory, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const filteredCategoryArticles = slug ? getArticlesByCategory(slug) : [];
  const categoryArticles = filteredCategoryArticles.length ? filteredCategoryArticles : getArticlesByCategory(categories[0]?.slug || slug || 'all');

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">分类不存在</h1>
            <Link to="/" className="text-accent-600 hover:text-accent-700">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // For categories with few articles, also show trending articles from other categories
  const displayArticles = categoryArticles.length >= 3
    ? categoryArticles
    : [...categoryArticles, ...articles.filter(a => a.categorySlug !== slug).slice(0, 6 - categoryArticles.length)];

  const featuredArticle = displayArticles[0];
  const gridArticles = displayArticles.slice(1);

  return (
    <>
      <Navbar />
      <main>
        {/* Category Header */}
        <section className="w-full pt-28 pb-12 md:pt-32 md:pb-16 bg-background-50">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
            <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-12 mt-6">
              <div className="md:w-[380px] flex-shrink-0">
                <div className="w-14 h-14 flex items-center justify-center bg-accent-100 rounded-2xl mb-4">
                  <i className={`${currentCategory.icon} text-2xl text-accent-600`}></i>
                </div>
                <h1
                  className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {currentCategory.name}
                </h1>
              </div>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-md pt-2">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full pb-6 bg-background-50 border-b border-background-200/60">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm transition-colors cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="w-full py-10 md:py-16 bg-background-100">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {featuredArticle && (
                <div className="lg:col-span-1 lg:row-span-2">
                  <ArticleCard article={featuredArticle} variant="featured" />
                </div>
              )}
              {gridArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="default" />
              ))}
            </div>

            {displayArticles.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - 52吃瓜网`,
            description: currentCategory.description,
            url: `http://bi365.cn/category/${slug}`,
            isPartOf: { '@id': 'http://bi365.cn/#website' },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'http://bi365.cn/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: currentCategory.name,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}